import { useState } from 'react';
import { BRAND, FONTS } from '../styles/tokens';
import { Reveal, SectionLabel } from './ui/Reveal';
import { SPEAKING, SPEAKER_KIT, formatInvestment } from '../data/speaking';
import { SPEAKING_INTEREST } from '../data/content';
import { useIsMobile } from '../hooks/useIsMobile';
import { track } from '../services/analytics';
import type { LeadPrefill, SignatureTalk } from '../types';

interface SpeakingProps { onLead: (p: LeadPrefill) => void }

function talkPrefill(t: SignatureTalk): LeadPrefill {
  return { interest: SPEAKING_INTEREST, message: `Talk of interest: ${t.title}` };
}

export function Speaking({ onLead }: SpeakingProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const m = useIsMobile();
  const primaryBtn = {
    background: BRAND.warm, color: BRAND.navy, border: 'none',
    padding: m ? '14px 24px' : '12px 24px', borderRadius: m ? 10 : 6,
    fontSize: 13, fontWeight: 700, fontFamily: FONTS.body, cursor: 'pointer',
    width: m ? '100%' : 'auto', transition: 'all 0.2s',
  } as const;

  return (
    <section id="speaking" style={{ background: BRAND.white, padding: m ? '64px 20px' : '100px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: m ? 32 : 52, maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }}>
            <SectionLabel center>Book Tracy to Speak</SectionLabel>
            <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 40, fontWeight: 700, color: BRAND.navy, lineHeight: 1.18, marginBottom: 16 }}>{SPEAKING.headline}</h2>
            <p style={{ fontFamily: FONTS.body, fontSize: m ? 15 : 17, lineHeight: 1.7, color: BRAND.textMid }}>{SPEAKING.positioning}</p>
          </div>
        </Reveal>

        <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(3, 1fr)', gap: m ? 14 : 20 }}>
          {SPEAKING.talks.map((t, i) => {
            const hot = hovered === t.id;
            return (
              <Reveal key={t.id} delay={m ? 0 : i * 0.08}>
                <article onMouseEnter={() => setHovered(t.id)} onMouseLeave={() => setHovered(null)}
                  aria-labelledby={`talk-${t.id}`}
                  style={{
                    background: hot ? BRAND.warmGlow : BRAND.cream,
                    border: `1.5px solid ${hot ? BRAND.warm : BRAND.border}`,
                    borderRadius: 14, padding: m ? 22 : 28, height: '100%', display: 'flex', flexDirection: 'column',
                    transition: 'all 0.25s', transform: !m && hot ? 'translateY(-3px)' : 'none',
                    boxShadow: hot ? '0 8px 24px rgba(0,0,0,0.06)' : 'none',
                  }}>
                  <div style={{ fontFamily: FONTS.body, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: BRAND.warm, fontWeight: 600, marginBottom: 10 }}>{t.audience}</div>
                  <h3 id={`talk-${t.id}`} style={{ fontFamily: FONTS.heading, fontSize: m ? 20 : 22, fontWeight: 700, color: BRAND.navy, lineHeight: 1.25, marginBottom: 10 }}>{t.title}</h3>
                  <p style={{ fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.7, color: BRAND.textMid, marginBottom: 16 }}>{t.hook}</p>
                  <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: BRAND.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Attendees will</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
                    {t.takeaways.map((d, j) => (
                      <li key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                        <div style={{ width: 5, height: 5, borderRadius: '50%', background: BRAND.warm, flexShrink: 0, marginTop: 8 }} />
                        <span style={{ fontFamily: FONTS.body, fontSize: 13, color: BRAND.text, lineHeight: 1.6 }}>{d}</span>
                      </li>
                    ))}
                  </ul>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                    {t.formats.map(f => (
                      <span key={f} style={{ fontFamily: FONTS.body, fontSize: 11, padding: '5px 10px', borderRadius: 24, background: `${BRAND.warm}12`, color: BRAND.warmDark, fontWeight: 600 }}>{f}</span>
                    ))}
                  </div>
                  {t.duration && <div style={{ fontFamily: FONTS.body, fontSize: 12, color: BRAND.textLight, marginBottom: 16 }}>⏱ {t.duration}</div>}
                  <div style={{ marginTop: 'auto' }}>
                    <button onClick={() => { track('speaking_cta_click', { talk: t.id }); onLead(talkPrefill(t)); }} style={primaryBtn}
                      onMouseEnter={e => { e.currentTarget.style.background = BRAND.warmDark; e.currentTarget.style.color = BRAND.white; }}
                      onMouseLeave={e => { e.currentTarget.style.background = BRAND.warm; e.currentTarget.style.color = BRAND.navy; }}>
                      Book this talk →
                    </button>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div style={{ marginTop: m ? 32 : 48, textAlign: 'center' }}>
            <p style={{ fontFamily: FONTS.body, fontSize: 14, color: BRAND.textLight, marginBottom: 16 }}>{formatInvestment(SPEAKING.investment)} Travel from Indiana is minimal for regional events.</p>
            <button onClick={() => { track('speaking_cta_click', { talk: 'any' }); onLead({ interest: SPEAKING_INTEREST }); }}
              style={{ background: BRAND.navy, color: BRAND.white, border: 'none', padding: m ? '18px 32px' : '16px 40px', borderRadius: m ? 12 : 8, fontSize: 16, fontWeight: 700, fontFamily: FONTS.body, cursor: 'pointer', width: m ? '100%' : 'auto' }}>
              Check Availability →
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Speaker Kit ─── */

const KIT_PANELS = [
  { id: 'short', label: 'Short bio (50 words)', key: 'bioShort' as const },
  { id: 'long', label: 'Long bio (250 words)', key: 'bioLong' as const },
  { id: 'intro', label: 'Emcee introduction', key: 'introScript' as const },
];

export function SpeakerKit() {
  const [open, setOpen] = useState<string | null>('short');
  const [copied, setCopied] = useState<string | null>(null);
  const m = useIsMobile();

  const copy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    } catch { /* noop */ }
  };

  return (
    <section id="speaker-kit" style={{ background: BRAND.cream, padding: m ? '64px 20px' : '100px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1.4fr', gap: m ? 32 : 56, alignItems: 'start' }}>
        <Reveal>
          <SectionLabel>Speaker Kit</SectionLabel>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 36, fontWeight: 700, color: BRAND.navy, lineHeight: 1.18, marginBottom: 16 }}>Everything a program chair needs.</h2>
          <p style={{ fontFamily: FONTS.body, fontSize: m ? 14 : 15, lineHeight: 1.8, color: BRAND.textMid, marginBottom: 24 }}>Bio, introduction, one-sheet, and video — ready to forward to your committee.</p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 24 }}>
            {SPEAKER_KIT.headshotUrl ? (
              <img src={SPEAKER_KIT.headshotUrl} alt="Tracy Curtis, M.A., CDVP" style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${BRAND.white}`, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
            ) : (
              <div aria-hidden style={{ width: 96, height: 96, borderRadius: '50%', background: `linear-gradient(135deg, ${BRAND.warm}, ${BRAND.teal})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND.white, fontFamily: FONTS.heading, fontSize: 34, fontWeight: 700, flexShrink: 0 }}>TC</div>
            )}
            <div>
              <div style={{ fontFamily: FONTS.heading, fontSize: 22, fontWeight: 700, color: BRAND.navy }}>Tracy Curtis, M.A., CDVP</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 13, color: BRAND.warm, fontWeight: 600 }}>Speaker · Consultant · Coercive Control Expert</div>
            </div>
          </div>

          <a href={SPEAKER_KIT.oneSheetUrl} target="_blank" rel="noopener noreferrer" onClick={() => track('one_sheet_download')}
            style={{ display: 'inline-block', background: BRAND.warm, color: BRAND.navy, fontFamily: FONTS.body, fontSize: m ? 16 : 15, fontWeight: 700, padding: m ? '16px 28px' : '14px 32px', borderRadius: m ? 12 : 8, textDecoration: 'none', width: m ? '100%' : 'auto', textAlign: 'center', boxSizing: 'border-box' }}>
            Download the Speaker One-Sheet (PDF) ↓
          </a>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
            {SPEAKER_KIT.links.map(l => (
              <a key={l.href} href={l.href} target={l.href.startsWith('mailto:') ? undefined : '_blank'} rel="noopener noreferrer"
                style={{ fontFamily: FONTS.body, fontSize: 14, color: BRAND.warmDark, textDecoration: 'none', fontWeight: 500 }}>→ {l.label}</a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={m ? 0 : 0.12}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {KIT_PANELS.map(p => {
              const isOpen = open === p.id;
              const text = SPEAKER_KIT[p.key];
              return (
                <div key={p.id} style={{ background: BRAND.white, border: `1px solid ${isOpen ? BRAND.warm + '40' : BRAND.border}`, borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.3s' }}>
                  <button onClick={() => setOpen(isOpen ? null : p.id)} aria-expanded={isOpen}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: m ? '16px 18px' : '18px 22px', background: isOpen ? BRAND.warmGlow : 'transparent', border: 'none', cursor: 'pointer', transition: 'background 0.3s', textAlign: 'left' }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: m ? 14 : 15, fontWeight: 600, color: BRAND.navy }}>{p.label}</span>
                    <span style={{ color: BRAND.warm, fontSize: 20, transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>+</span>
                  </button>
                  {isOpen && (
                    <div style={{ padding: m ? '0 18px 16px' : '0 22px 20px' }}>
                      <p style={{ fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.8, color: BRAND.textMid, whiteSpace: 'pre-line', margin: '0 0 12px' }}>{text}</p>
                      <button onClick={() => copy(p.id, text)}
                        style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: copied === p.id ? BRAND.teal : BRAND.warmDark, background: 'transparent', border: `1.5px solid ${copied === p.id ? BRAND.teal : BRAND.border}`, borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
                        {copied === p.id ? '✓ Copied' : 'Copy text'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
